import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import proxy from "../../proxy";
import {
  Box,
  Container,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  HStack,
  Badge,
  useDisclosure,
  useToast,
  Text,
  VStack,
  Spinner,
  Center,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import {
  EditIcon,
  DeleteIcon,
  AddIcon,
  ArrowBackIcon,
  SearchIcon,
} from "@chakra-ui/icons";
import WalletModal from "./WalletModal";

const AdminWallets = () => {
  const [wallets, setWallets] = useState([]);
  const [filteredWallets, setFilteredWallets] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();

  // Fetch wallets
  const fetchWallets = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${proxy}server_mts/admin/GetAllWallets.php`);
      setWallets(response.data);
      setFilteredWallets(response.data);
    } catch (error) {
      toast({
        title: "Error loading wallets",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search filter
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredWallets(wallets);
    } else {
      const filtered = wallets.filter(
        (w) =>
          w.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
          w.symbol.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredWallets(filtered);
    }
  }, [searchQuery, wallets]);

  // Add wallet
  const handleAdd = () => {
    setSelectedWallet(null);
    onOpen();
  };

  // Edit wallet
  const handleEdit = (wallet) => {
    setSelectedWallet(wallet);
    onOpen();
  };

  // Delete wallet
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this wallet?")) {
      return;
    }

    try {
      await axios.post(`${proxy}server_mts/admin/DeleteWallet.php`, { id });
      toast({
        title: "Wallet deleted successfully",
        status: "success",
        duration: 2000,
        position: "top",
      });
      fetchWallets();
    } catch (error) {
      toast({
        title: "Error deleting wallet",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    }
  };

  // Save wallet (add or update)
const handleSave = async () => {
  onClose();
  // ✅ ADAUGĂ await și setIsLoading
  setIsLoading(true);
  await fetchWallets();
  setIsLoading(false);
};

  if (isLoading) {
    return (
      <Box minH="100vh" bg="gray.900">
        <Center minH="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="teal.400" thickness="4px" />
            <Text color="gray.400">Loading wallets...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.900">
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.95))"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        py={4}
        px={6}
        boxShadow="0 4px 20px rgba(0,0,0,0.3)"
      >
        <Container maxW="1400px">
          <HStack justify="space-between">
            <HStack spacing={4}>
              <IconButton
                icon={<ArrowBackIcon />}
                variant="ghost"
                colorScheme="teal"
                onClick={() => navigate("/admin/dashboard")}
                aria-label="Back to dashboard"
              />
              <VStack align="start" spacing={0}>
                <Heading
                  size="md"
                  bgGradient="linear(to-r, teal.300, cyan.400)"
                  bgClip="text"
                >
                  💎 Wallet Management
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Manage crypto wallet addresses
                </Text>
              </VStack>
            </HStack>

            <Button
              leftIcon={<AddIcon />}
              colorScheme="teal"
              onClick={handleAdd}
              size="sm"
            >
              Add Wallet
            </Button>
          </HStack>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxW="1400px" py={6}>
        <VStack spacing={6} align="stretch">
          {/* Search & Stats */}
          <HStack justify="space-between" spacing={4}>
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search by address or symbol..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
                color="white"
              />
            </InputGroup>

            <Badge colorScheme="teal" fontSize="sm" px={3} py={1}>
              {filteredWallets.length} wallet{filteredWallets.length !== 1 ? "s" : ""}
            </Badge>
          </HStack>

          {/* Table */}
          <Box
            bg="linear-gradient(135deg, rgba(26,32,44,0.97), rgba(45,55,72,0.97))"
            borderRadius="xl"
            border="1px solid"
            borderColor="whiteAlpha.200"
            overflow="hidden"
            boxShadow="0 10px 30px rgba(0,0,0,0.3)"
          >
            {filteredWallets.length === 0 ? (
              <Center py={12}>
                <VStack spacing={3}>
                  <Text fontSize="4xl">📭</Text>
                  <Text color="gray.400" fontSize="lg">
                    {searchQuery ? "No wallets found" : "No wallets yet"}
                  </Text>
                  <Text color="gray.500" fontSize="sm">
                    {searchQuery
                      ? "Try a different search term"
                      : "Click 'Add Wallet' to get started"}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <Table variant="simple">
  <Thead bg="whiteAlpha.50">
    <Tr>
      <Th color="teal.300" borderColor="whiteAlpha.200">ID</Th>
      <Th color="teal.300" borderColor="whiteAlpha.200">Address</Th>
      <Th color="teal.300" borderColor="whiteAlpha.200">Symbol</Th>
      <Th color="teal.300" borderColor="whiteAlpha.200">Coin</Th>
      <Th color="teal.300" borderColor="whiteAlpha.200">Amount</Th>
      <Th color="teal.300" borderColor="whiteAlpha.200">Logo</Th>
      <Th color="teal.300" borderColor="whiteAlpha.200">Explorer</Th>
      <Th color="teal.300" borderColor="whiteAlpha.200" textAlign="right">Actions</Th>
    </Tr>
  </Thead>
  <Tbody>
    {filteredWallets.map((wallet) => (
      <Tr key={wallet.id} _hover={{ bg: "whiteAlpha.50" }} transition="0.2s">
        <Td color="gray.400" borderColor="whiteAlpha.100">#{wallet.id}</Td>
        <Td color="white" fontFamily="monospace" fontSize="sm" borderColor="whiteAlpha.100">
          {wallet.address.substring(0, 8)}...{wallet.address.substring(wallet.address.length - 8)}
        </Td>
        <Td borderColor="whiteAlpha.100">
          <Badge colorScheme="purple">{wallet.symbol}</Badge>
        </Td>
        <Td color="gray.300" borderColor="whiteAlpha.100">{wallet.coin || "N/A"}</Td>
        <Td color="green.300" fontWeight="bold" borderColor="whiteAlpha.100">
          {parseFloat(wallet.amount).toFixed(8)}
        </Td>
        <Td color="gray.400" borderColor="whiteAlpha.100">{wallet.logo || "N/A"}</Td>
        <Td color="gray.400" borderColor="whiteAlpha.100">{wallet.explorer || "N/A"}</Td>
        <Td borderColor="whiteAlpha.100" textAlign="right">
          <HStack spacing={2} justify="flex-end">
            <IconButton
              icon={<EditIcon />}
              size="sm"
              colorScheme="blue"
              variant="ghost"
              onClick={() => handleEdit(wallet)}
              aria-label="Edit wallet"
            />
            <IconButton
              icon={<DeleteIcon />}
              size="sm"
              colorScheme="red"
              variant="ghost"
              onClick={() => handleDelete(wallet.id)}
              aria-label="Delete wallet"
            />
          </HStack>
        </Td>
      </Tr>
    ))}
  </Tbody>
</Table>
            )}
          </Box>
        </VStack>
      </Container>

      {/* Modal */}
      <WalletModal
        isOpen={isOpen}
        onClose={onClose}
        wallet={selectedWallet}
        onSave={handleSave}
      />
    </Box>
  );
};

export default AdminWallets;